import React, { useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import { AppContainer } from './components/Container';
import { VisaForm } from './components/VisaForm';
import { VisaStatus } from './components/VisaStatus';
import { useVisaTimer } from './hooks/useVisaTimer';
import ErrorBoundary from './components/ErrorBoundary';
import TimeService from './services/timeService';

function App() {
  const [selectedDate, setSelectedDate] = useState('');
  const [visaDuration, setVisaDuration] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [errors, setErrors] = useState({});
  const [timeAccuracy, setTimeAccuracy] = useState(null);

  // Initialize time service on app load
  useEffect(() => {
    const timeService = TimeService;
    
    // Start auto-sync (with smart gating built-in)
    timeService.startAutoSync();

    // Subscribe to time updates
    const unsubscribe = timeService.subscribe((timeInfo) => {
      setTimeAccuracy(timeInfo.accuracy);
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
      timeService.stopAutoSync();
    };
  }, []);

  const {
    isConfirmed,
    status,
    remainingTime,
    expiryDate,
    localTime,
    localDate,
    localCity,
    localTimezone,
    confirmVisa,
    resetVisa
  } = useVisaTimer(selectedDate, visaDuration, selectedCountry);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setErrors(prev => ({ ...prev, date: null }));
  };

  const handleDurationChange = (duration) => {
    setVisaDuration(duration);
    setErrors(prev => ({ ...prev, duration: null }));
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setErrors(prev => ({ ...prev, country: null }));
  };


  const handleConfirm = () => {
    const success = confirmVisa();
    if (!success) {
      setErrors({
        country: !selectedCountry ? 'Please select a country' : null,
        date: !selectedDate ? 'Please select a visa issue date' : null,
        duration: !visaDuration || visaDuration <= 0 ? 'Please enter a valid visa duration' : null
      });
    }
  };


  const handleReset = () => {
    setSelectedDate('');
    setVisaDuration('');
    setSelectedCountry(null);
    setErrors({});
    resetVisa();
  };

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <AppContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {isConfirmed ? (
            <VisaStatus
              visaDuration={visaDuration}
              selectedDate={selectedDate}
              selectedCountry={selectedCountry}
              status={status}
              expiryDate={expiryDate}
              remainingTime={remainingTime}
              localTime={localTime}
              localDate={localDate}
              localCity={localCity}
              localTimezone={localTimezone}
              onReset={handleReset}
              timeAccuracy={timeAccuracy}
            />
          ) : (
            <VisaForm
              selectedDate={selectedDate}
              visaDuration={visaDuration}
              selectedCountry={selectedCountry}
              onDateChange={handleDateChange}
              onDurationChange={handleDurationChange}
              onCountryChange={handleCountryChange}
              onConfirm={handleConfirm}
              onReset={handleReset}
              errors={errors}
              timeAccuracy={timeAccuracy}
            />
          )}
          <Analytics />
          <SpeedInsights />
        </AppContainer>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;